import { ref } from 'vue';

import useHasSmartOnboardingAccess from '@/composables/useHasSmartOnboardingAccess';
import { FILE_UPLOAD_DATA_SOURCE_TYPE } from '@/constants/data_sources';
import { ls } from '@/local_storage';
import { Feed } from '@/types/feeds';
import { Population } from '@/types/populations';
import { Report, TrimmedReport } from '@/types/reports';
import { User } from '@/types/root';
import { Source } from '@/types/sources';

type ReportLike =
  | Record<string, unknown>
  | Pick<Report, 'origin'>
  | Pick<TrimmedReport, 'origin'>
  | null
  | undefined;

type SourceLike =
  | Record<string, unknown>
  | Pick<Source, 'origin'>
  | null
  | undefined;

type PopulationLike =
  | Record<string, unknown>
  | Pick<Population, 'origin'>
  | null
  | undefined;

/**
 * Single source of truth for "is this the onboarding demo report?". Used by
 * the guided tour, the lists table, and the report detail bypasses.
 */
export function isOnboardingDemoReport(report: ReportLike): boolean {
  return !!report && 'origin' in report && report.origin === 'system';
}

/**
 * Single source of truth for "is this a system-built (onboarding demo) source?".
 * The onboarding flow auto-creates a CSV source with `origin: 'system'` that
 * must be ignored when deciding whether the user has connected real data.
 */
export function isOnboardingDemoSource(source: SourceLike): boolean {
  return !!source && 'origin' in source && source.origin === 'system';
}

/**
 * Single source of truth for "is this a system-built (onboarding demo)
 * population?". The onboarding flow seeds a demo report whose population has
 * `origin: 'system'`; its sharing must be ignored when deciding whether the
 * user has set up sharing for their own data.
 */
export function isOnboardingDemoPopulation(
  population: PopulationLike,
): boolean {
  return (
    !!population && 'origin' in population && population.origin === 'system'
  );
}

/**
 * Single source of truth for "is this a system-built (onboarding demo) feed?".
 * The demo source always lives on a CSV (file upload) feed, so a feed is
 * demo-built when it is a file upload whose sources are all system-built.
 */
export function isOnboardingDemoFeed(
  feed: Feed,
  feedSources: Source[],
): boolean {
  if (feed.integration.type !== FILE_UPLOAD_DATA_SOURCE_TYPE) return false;
  return feedSources.length > 0 && feedSources.every(isOnboardingDemoSource);
}

/**
 * Gates every onboarding-demo behavior (tour, paywall bypass, free-tier
 * access) behind the same feature flag that drives the new smart onboarding
 * flow. When the flag is off, the demo report behaves like any other report.
 */
export function useIsOnboardingDemoEnabled() {
  return useHasSmartOnboardingAccess();
}

/**
 * Reactive signal bumped by {@link markOnboardingDemoTourSeen} so that Vue
 * computeds calling {@link hasSeenOnboardingDemoTour} re-evaluate. Without
 * this, localStorage writes are invisible to Vue's reactivity system.
 */
const tourSeenSignal = ref(0);

/**
 * Whether the user has already seen / dismissed the onboarding demo tour.
 * Persistence is FE-only via localStorage — the BE intentionally does not
 * track this. A user clearing their storage (or using a different device
 * / browser) seeing the tour again is acceptable.
 */
export function hasSeenOnboardingDemoTour(
  user: User | null | undefined,
): boolean {
  void tourSeenSignal.value;
  if (!user?.id) return false;
  return ls.onboardingDemoTourSeen.get() === user.id;
}

/**
 * Marks the tour as seen for the given user. Called on Done (step 4) and
 * Skip (X). Stores the user id so other users on the same device still see
 * the tour on their first visit.
 */
export function markOnboardingDemoTourSeen(
  user: User | null | undefined,
): void {
  if (!user?.id) return;
  ls.onboardingDemoTourSeen.set(user.id);
  tourSeenSignal.value++;
}
